"use client";
import { useState, useRef } from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { ButtonGroup } from "@/shared/components";
import { CONTENT_FRAME } from "@/shared/components/BlockWrapper";
import type { ButtonDataType, ImageDataType } from "@/shared/types";
import { cn } from "@/lib/utils";
import { HeroTagsRow } from "./HeroTagsRow";
import { HeroTitle } from "./HeroTitle";
import { getSectionBorderClass } from "./blockSection";
import {
  getHeroSlideThemeClasses,
  resolveHeroSlideTheme,
  type HeroSlideTheme,
} from "./heroSlideTheme";
import "swiper/css";

export interface HeroSlide {
  tags: string[];
  headline: string;
  description?: string | null;
  image: ImageDataType | null;
  buttons?: ButtonDataType[];
  theme?: HeroSlideTheme | null;
}

interface HeroSliderProps {
  slides: HeroSlide[];
}

/** Figma: mobile 375×280 mask → чуть квадратнее; tablet banner 768×350. */
const HERO_IMAGE_CLASS =
  "relative w-full overflow-hidden aspect-square md:aspect-auto md:min-h-[350px]";

export function HeroSlider({ slides }: HeroSliderProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);

  if (!slides.length) return null;

  const safeActiveIndex =
    activeIndex >= 0 && activeIndex < slides.length ? activeIndex : 0;
  const activeSlide = slides[safeActiveIndex];

  if (!activeSlide) {
    return null;
  }

  const activeTheme = resolveHeroSlideTheme(activeSlide.theme);
  const themeClasses = getHeroSlideThemeClasses(activeTheme);

  const handleSlideChange = (swiper: SwiperType) => {
    setFading(true);
    setTimeout(() => {
      setActiveIndex(swiper.realIndex);
      setFading(false);
    }, 200);
  };

  const goToSlide = (index: number) => {
    swiperRef.current?.slideToLoop(index);
  };

  const renderPagination = (className?: string) => (
    <div
      className={cn(
        "flex h-2 items-center gap-4 desktop:gap-2",
        className
      )}
      role="tablist"
      aria-label="Слайды"
    >
      {slides.map((_, index) => (
        <button
          key={index}
          type="button"
          role="tab"
          aria-selected={index === safeActiveIndex}
          aria-label={`Слайд ${index + 1}`}
          onClick={() => goToSlide(index)}
          className={cn(
            "size-2 rounded-full border transition-colors duration-300",
            index === safeActiveIndex
              ? themeClasses.paginationDotActive
              : themeClasses.paginationDotInactive,
            index !== safeActiveIndex && "hover:opacity-70"
          )}
        />
      ))}
    </div>
  );

  const renderStackedImage = (slide: HeroSlide, priority: boolean) => {
    if (!slide.image) {
      return <div className={cn(HERO_IMAGE_CLASS, "bg-muted")} />;
    }

    return (
      <div className={HERO_IMAGE_CLASS}>
        <Image
          className="object-cover"
          src={slide.image.src}
          alt={slide.image.alt || ""}
          fill
          sizes="100vw"
          priority={priority}
        />
      </div>
    );
  };

  return (
    <section
      aria-label="Заголовок страницы"
      className={cn("transition-colors duration-300", themeClasses.section)}
    >
      <div
        className={cn(
          CONTENT_FRAME,
          "flex flex-col desktop:h-[600px] desktop:min-h-[600px] desktop:flex-row desktop:items-stretch"
        )}
      >
        <div
          className={cn(
            "relative flex w-full flex-col gap-6 px-4 py-10 md:gap-8 md:px-6 md:pb-12 md:pt-10",
            getSectionBorderClass(),
            "border-b desktop:border-b-0 desktop:border-r",
            "desktop:h-full desktop:w-1/2 desktop:gap-12 desktop:px-10 desktop:py-20"
          )}
        >
          <div
            className={cn(
              "flex flex-col gap-6 transition-opacity duration-200 desktop:min-h-0 desktop:flex-1",
              fading ? "opacity-0" : "opacity-100"
            )}
          >
            <div className="flex flex-col gap-4">
              <HeroTagsRow
                tags={activeSlide.tags}
                variant={themeClasses.tagVariant}
              />

              <HeroTitle className={themeClasses.heading}>
                {activeSlide.headline}
              </HeroTitle>
            </div>

            {activeSlide.description && (
              <p
                className={cn(
                  "max-w-[640px] font-sans text-base font-normal leading-[1.2]",
                  themeClasses.body
                )}
              >
                {activeSlide.description}
              </p>
            )}

            {activeSlide.buttons && activeSlide.buttons.length > 0 && (
              <ButtonGroup items={activeSlide.buttons} className="mt-2 flex-wrap" />
            )}
          </div>

          {slides.length > 1 && renderPagination("desktop:hidden")}

          {slides.length > 1 &&
            renderPagination("absolute bottom-20 left-10 hidden desktop:flex")}
        </div>

        <div className="w-full desktop:hidden">{renderStackedImage(activeSlide, true)}</div>

        <div className="relative hidden min-h-[400px] w-full overflow-hidden desktop:block desktop:h-full desktop:w-1/2 desktop:min-h-[600px]">
          <Swiper
            modules={[Autoplay]}
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            onSlideChange={handleSlideChange}
            autoplay={{
              delay: 4500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            loop={slides.length > 1}
            spaceBetween={0}
            slidesPerView={1}
            className="h-full w-full desktop:min-h-[600px]"
          >
            {slides.map((slide, index) => (
              <SwiperSlide key={index} className="relative !h-full min-h-[inherit]">
                {slide.image ? (
                  <Image
                    className="object-cover"
                    src={slide.image.src}
                    alt={slide.image.alt || ""}
                    fill
                    sizes="50vw"
                    priority={index === 0}
                  />
                ) : (
                  <div className="h-full w-full bg-muted" />
                )}
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </div>
    </section>
  );
}
